import React from 'react';
import { Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import Flex from 'components/common/Flex';
import Avatar from 'components/common/Avatar';
import paths from 'routes/paths';
import moment from 'moment';
import SubtleBadge from 'components/common/SubtleBadge';

const CustomerLog = ({ customerJobList }) => {
  const Shortened = address => {
    // Convert address to string
    const addressStr = address;

    // Check if addressStr is at least 7 characters long (for example)
    // You can adjust this condition based on your specific needs
    if (addressStr.length >= 7) {
      // Extract the first three characters
      const first = addressStr.substring(0, 15);

      // Extract the last three characters
      const last = addressStr.substring(addressStr.length - 10);

      // Create the shortened format
      const shortenedFormat = `${first}....${last}`;

      // Render the shortened format
      return shortenedFormat;
    } else {
      // If addressStr is shorter than 7 characters, return the original address
      return addressStr;
    }
  };

  const columns = [
    {
      accessorKey: 'title',
      header: 'Title',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original } }) => {
        const { title } = original;
        return <h5 className="mb-0 fs-10">{title}</h5>;
      }
    },
    {
      accessorKey: 'start',
      header: 'Start',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original } }) => {
        const { start } = original;
        return (
          <h5 className="mb-0 fs-10">
            {moment(start).format('MMMM DD YYYY hh:mm a')}
          </h5>
        );
      }
    },
    {
      accessorKey: 'end',
      header: 'End',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original } }) => {
        const { end } = original;
        return (
          <h5 className="mb-0 fs-10">
            {moment(end).format('MMMM DD YYYY hh:mm a')}
          </h5>
        );
      }
    },
    {
      accessorKey: 'address',
      header: 'Address',
      meta: {
        headerProps: {
          style: { minWidth: '200px' },
          className: 'ps-5 text-900'
        },
        cellProps: { className: 'ps-5' }
      },
      cell: ({ row: { original } }) => {
        const { address } = original;
        return <h5 className="mb-0 fs-10">{Shortened(address)}</h5>;
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-900' },
        cellProps: { className: '' }
      },
      cell: ({ row: { original } }) => {
        const { status } = original;
        return (
          <SubtleBadge
            pill
            bg={
              status === 'Missed' || status === 'Pending Clock Out'
                ? 'danger'
                : status === 'Nurse Assigned' || status === 'In-Progress'
                ? 'success'
                : status === 'Unfulfilled'
                ? 'secondary'
                : 'primary'
            }
          >
            {status}
          </SubtleBadge>
        );
      }
    },
    {
      accessorKey: 'nurse',
      header: 'Assigned',
      meta: {
        headerProps: { className: 'pe-1 text-900' },
        cellProps: {
          className: 'py-2'
        }
      },
      cell: ({ row: { original } }) => {
        const { nurse } = original;
        return (
          <>
            {nurse ? (
              <Link to={paths.nursd_profile(nurse?.id, 'profile')}>
                <Flex alignItems="center">
                  {nurse?.profileImage ? (
                    <Avatar
                      src={nurse?.profileImage}
                      size="xl"
                      className="me-2"
                    />
                  ) : (
                    <Avatar size="xl" name={nurse?.name} className="me-2" />
                  )}
                  <div className="flex-1">
                    <h5 className="mb-0 fs-10 text-primary">{nurse?.name}</h5>
                    <h5 className="mb-0 fs-10">{nurse?.licenseType}</h5>
                  </div>
                </Flex>
              </Link>
            ) : (
              <h5 className="mb-0 fs-10">-</h5>
            )}
          </>
        );
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original } }) => {
        const { createdAt } = original;
        return (
          <h5 className="mb-0 fs-10">
            {moment(createdAt).format('MMMM DD YYYY hh:mm a')}
          </h5>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: customerJobList,
    columns,
    selection: true,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <Card className="mb-3">
          <Card.Header>
            <h5>Job Order List</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <AdvanceTable
              headerClassName="bg-200 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                size: 'sm',
                striped: true,
                className: 'fs-10 mb-0 overflow-hidden'
              }}
            />
          </Card.Body>
          <Card.Footer>
            <div className="mt-3">
              <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
            </div>
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>
    </>
  );
};

CustomerLog.propTypes = {
  customerJobList: PropTypes.any
};

export default CustomerLog;
